<header class="flex header p-2">
  <h2>{{ 'Change_Dyeing_Machine_Modal_Title' | translateWF: 'Change Dyeing Machine' }}</h2>
</header>

<section class="content">
  <div class="col">
    <!-- Current Machine & Assigned Rolls Summary -->
    <p-panel header="Summary">
      <div class="grid-auto-2">
        @if (currentAsset) {
          <p-panel [header]="'Change_Dyeing_Machine_Modal_Current_Machine' | translateWF: 'Current Machine'" data-tone="surface">
            <div class="current-machine-info p-3 border rounded-md bg-slate-100">
              <div class="flex justify-between items-center">
                <span class="font-semibold">{{ currentAsset.assetId }}</span>
                <span class="text-gray-600">{{ currentAsset.assetModelName }}</span>
              </div>
              <div class="flex gap-4 mt-2 text-sm text-gray-600">
                <span>{{ 'Ports' | translateWF: 'Ports' }}: {{ currentAsset.dyeingMachineDetails?.numberOfPorts ?? 0 | number }}</span>
                <span>{{ 'Change_Dyeing_Machine_Modal_Capacity_Lbs' | translateWF: 'Capacity (lbs)' }}: {{ getCapacityInLbs(currentAsset) | number: '1.2-2' }}</span>
              </div>
            </div>
          </p-panel>
        }

        <!-- Assigned Rolls Summary -->
        <p-panel [header]="'Change_Dyeing_Machine_Modal_Assigned_Rolls' | translateWF: 'Assigned Rolls'" data-tone="surface">
          <div class="p-3 border rounded-md bg-blue-50">
            <div class="text-2xl font-bold text-blue-700">{{ totalAssignedLbs | number: '1.2-2' }} lbs</div>
            <div class="text-sm text-gray-600 mt-1">
              @if (hasWeighedBundles()) {
                {{ 'Change_Dyeing_Machine_Modal_Total_Bundle_Weight' | translateWF: 'Total bundle weight' }}
              } @else {
                {{ 'Change_Dyeing_Machine_Modal_Total_Weight' | translateWF: 'Total weight of rolls assigned to bundles' }}
              }
            </div>
          </div>
        </p-panel>
      </div>
    </p-panel>

    <!-- Bundles Section -->
    @if (bundles.length > 0) {
      <div class="bundles-section">
        <h3 class="section-title mb-2">{{ 'Bundles' | translateWF: 'Bundles' }} ({{ bundles.length }})</h3>
        <div class="border rounded-md overflow-hidden">
          <p-table [value]="bundleDisplayList()" styleClass="p-datatable-sm cs-datatable-flex-fill" [scrollable]="true" scrollHeight="200px">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'Bundle_Number' | translateWF: 'Bundle #' }}</th>
                <th>{{ 'Status' | translateWF: 'Status' }}</th>
                <th>{{ 'Rolls' | translateWF: 'Rolls' }}</th>
                <th>{{ 'Change_Dyeing_Machine_Modal_Weight_Lbs' | translateWF: 'Weight (lbs)' }}</th>
                <th style="width: 80px">{{ 'Actions' | translateWF: 'Actions' }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.bundle.number }}</td>
                <td>
                  <p-tag [severity]="item.status === 'weighed' ? 'success' : item.status === 'with-rolls' ? 'info' : 'secondary'" [value]="item.statusLabel" />
                </td>
                <td>{{ item.bundle.rollNumbers?.length ?? 0 }}</td>
                <td>
                  @if (item.status === 'weighed') {
                    {{ item.bundle.actualWeightInLbs | number: '1.2-2' }}
                  } @else if (item.rollsWeight > 0) {
                    {{ item.rollsWeight | number: '1.2-2' }}
                  } @else {
                    -
                  }
                </td>
                <td>
                  @if (item.canDelete) {
                    <p-button
                      icon="pi pi-trash"
                      severity="danger"
                      [text]="true"
                      size="small"
                      (click)="confirmDeleteBundle(item)"
                      [loading]="isDeletingBundle()"
                      [pTooltip]="'Delete_Empty_Bundle' | translateWF: 'Delete empty bundle'"
                    />
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>
    }

    <p-panel
      [header]="
        'Change_Dyeing_Machine_Modal_Select_New_Machine'
          | translateWF
            : 'Select New
        Machine'
      "
    >
      <div class="col-sm">
        <p-message severity="info">
          {{ 'Change_Dyeing_Machine_Modal_Machines_With_Capacity' | translateWF: 'Only machines with capacity for' }}
          {{ totalAssignedLbs | number: '1.2-2' }} lbs
          {{
            'Change_Dyeing_Machine_Modal_Are_Shown'
              | translateWF
                : 'are
          shown.'
          }}
        </p-message>

        @if (isLoading()) {
          <div class="row-center-center">
            <i class="pi pi-spin pi-spinner text-2xl"></i>
          </div>
        } @else {
          @if (eligibleMachines().length === 0) {
            <p-message severity="warn" styleClass="w-full">
              {{
                'Change_Dyeing_Machine_Modal_No_Eligible_Machines_Capacity'
                  | translateWF
                    : 'No eligible machines found with
          sufficient capacity for'
              }}
              {{ totalAssignedLbs | number: '1.2-2' }} lbs.
            </p-message>
          } @else {
            <p-card data-variant="compact">
              <p-table
                [value]="eligibleMachines()"
                selectionMode="single"
                [(selection)]="selectedMachineControl.value"
                (selectionChange)="selectedMachineControl.setValue($event)"
                [scrollable]="true"
                scrollHeight="flex"
                styleClass="p-datatable-sm cs-datatable-selectable-rows cs-datatable-flex-fill"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"></th>
                    <th>{{ 'Change_Dyeing_Machine_Modal_Machine_ID' | translateWF: 'Machine ID' }}</th>
                    <th>{{ 'Model' | translateWF: 'Model' }}</th>
                    <th>{{ 'Ports' | translateWF: 'Ports' }}</th>
                    <th>{{ 'Change_Dyeing_Machine_Modal_Capacity_Lbs' | translateWF: 'Capacity (lbs)' }}</th>
                    <th>{{ 'Change_Dyeing_Machine_Modal_Capacity_Kgs' | translateWF: 'Capacity (kgs)' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-machine>
                  <tr [pSelectableRow]="machine">
                    <td>
                      <p-tableRadioButton [value]="machine" />
                    </td>
                    <td>{{ machine.assetId }}</td>
                    <td>{{ machine.assetModelName }}</td>
                    <td>{{ machine.dyeingMachineDetails?.numberOfPorts ?? 0 | number }}</td>
                    <td>{{ getCapacityInLbs(machine) | number: '1.2-2' }}</td>
                    <td>{{ machine.assetCapacity?.maxCapacity ?? 0 | number: '1.2-2' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
            @if (selectedMachineControl.touched && selectedMachineControl.invalid) {
              <small class="text-red-500 mt-2 block">{{
                'Change_Dyeing_Machine_Modal_Please_Select_Machine'
                  | translateWF
                    : 'Please
          select a machine.'
              }}</small>
            }

            @if (getBundlesToDeleteCount() > 0) {
              <p-message severity="warn" styleClass="mt-3">
                {{ getBundlesToDeleteCount() }}
                {{
                  'Change_Dyeing_Machine_Modal_Bundles_Will_Be_Deleted'
                    | translateWF
                      : 'empty
          bundle(s) will be automatically deleted when changing to this machine.'
                }}
              </p-message>
            }
          }
        }
      </div>
    </p-panel>
  </div>
</section>

<footer>
  <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onClose()" />
  <p-button [label]="'Change_Dyeing_Machine_Modal_Change_Machine' | translateWF: 'Change Machine'" (click)="onSave()" [disabled]="eligibleMachines().length === 0 || isLoading()" />
</footer>

<p-confirmDialog />
